import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, apiOrigin } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Badge, Card, formatSum } from '@/components/ui';

/**
 * Дашборд собран вокруг одного вопроса: что требует действия прямо сейчас.
 * Поэтому операционные счётчики стоят выше выручки — деньги подождут,
 * а просроченная инспекция морозит залог клиента.
 */

interface Overview {
  range: { from: string; to: string };
  revenue: {
    gross: number;
    refunded: number;
    net: number;
    payments: number;
    byProvider: Array<{ provider: string; amount: number }>;
  };
  orders: {
    created: number;
    completed: number;
    cancelled: number;
    averageCheck: number;
    cancelRate: number;
  };
  statuses: Array<{ status: string; count: number }>;
  customers: { total: number; new: number; withOrders: number };
  operations: {
    awaitingConfirmation: number;
    awaitingPayment: number;
    activeRentals: number;
    dueToday: number;
    overdue: number;
    overdueInspections: number;
  };
  catalog: { products: number; active: number; withoutImages: number; withoutVariants: number };
}

interface ReadyResponse {
  status: 'up' | 'down';
  services: { database: 'up' | 'down'; redis: 'up' | 'down' };
}

interface TopProduct {
  productId: string;
  name: Record<string, string> | null;
  orders: number;
  qty: number;
  revenue: number;
}

export default function DashboardPage() {
  const { admin, can } = useAuth();
  const mayViewAnalytics = can('analytics.view');

  const overview = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => (await api.get<Overview>('/admin/analytics/overview')).data,
    enabled: mayViewAnalytics,
    refetchInterval: 60_000,
  });

  const top = useQuery({
    queryKey: ['analytics-top'],
    queryFn: async () => (await api.get<TopProduct[]>('/admin/analytics/top-products')).data,
    enabled: mayViewAnalytics,
  });

  const health = useQuery({
    queryKey: ['ready'],
    queryFn: async () => (await api.get<ReadyResponse>('/ready', { baseURL: apiOrigin })).data,
    refetchInterval: 30_000,
  });

  const o = overview.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Обзор</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {admin?.fullName}
            {o && ` · период ${o.range.from} — ${o.range.to}`}
          </p>
        </div>
        <Health data={health.data} loading={health.isLoading} />
      </header>

      {!mayViewAnalytics ? (
        <Card>
          <p className="text-sm text-neutral-500">
            Нет права <span className="font-mono">analytics.view</span> — сводка скрыта
          </p>
        </Card>
      ) : overview.isLoading ? (
        <Card>
          <p className="text-sm text-neutral-500">Загрузка…</p>
        </Card>
      ) : o ? (
        <>
          <section>
            <h2 className="mb-2 text-sm font-medium text-neutral-500">Требует внимания</h2>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Action
                label="Просрочен возврат"
                value={o.operations.overdue}
                to="/orders"
                alarming
              />
              <Action
                label="Просрочена проверка"
                value={o.operations.overdueInspections}
                to="/returns"
                alarming
              />
              <Action
                label="Ждут подтверждения"
                value={o.operations.awaitingConfirmation}
                to="/orders"
              />
              <Action label="Возврат сегодня" value={o.operations.dueToday} to="/returns" />
              <Action label="Ждут оплаты" value={o.operations.awaitingPayment} to="/payments" />
              <Action label="Аренд в работе" value={o.operations.activeRentals} to="/orders" />
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-3">
            <Card title="Выручка за 30 дней">
              <p className="text-2xl font-semibold">{formatSum(o.revenue.net)}</p>
              <p className="mt-1 text-sm text-neutral-500">
                проведено {o.revenue.payments} платежей
                {o.revenue.refunded > 0 && `, возвращено ${formatSum(o.revenue.refunded)}`}
              </p>
              <div className="mt-3 space-y-1 text-sm">
                {o.revenue.byProvider.map((row) => (
                  <div key={row.provider} className="flex justify-between">
                    <span className="text-neutral-500">{row.provider}</span>
                    <span>{formatSum(row.amount)}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Заказы за 30 дней">
              <p className="text-2xl font-semibold">{o.orders.created}</p>
              <div className="mt-3 space-y-1 text-sm">
                <Row label="Завершено" value={String(o.orders.completed)} />
                <Row label="Отменено" value={`${o.orders.cancelled} (${o.orders.cancelRate}%)`} />
                <Row label="Средний чек" value={formatSum(o.orders.averageCheck)} />
              </div>
            </Card>

            <Card title="Клиенты">
              <p className="text-2xl font-semibold">{o.customers.total}</p>
              <div className="mt-3 space-y-1 text-sm">
                <Row label="Новых за период" value={String(o.customers.new)} />
                <Row label="С заказами" value={String(o.customers.withOrders)} />
              </div>
            </Card>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <Card title="Заказы по статусам">
              {o.statuses.length === 0 ? (
                <p className="text-sm text-neutral-500">Заказов пока нет</p>
              ) : (
                <div className="space-y-1 text-sm">
                  {o.statuses
                    .slice()
                    .sort((a, b) => b.count - a.count)
                    .map((row) => (
                      <div key={row.status} className="flex justify-between">
                        <span className="font-mono text-xs text-neutral-500">{row.status}</span>
                        <span>{row.count}</span>
                      </div>
                    ))}
                </div>
              )}
            </Card>

            <Card title="Топ товаров за 30 дней">
              {(top.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-neutral-500">Пока нечего показать</p>
              ) : (
                <div className="space-y-1 text-sm">
                  {top.data!.map((row) => (
                    <div key={row.productId} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate">{row.name?.ru ?? '—'}</span>
                      <span className="shrink-0 text-neutral-500">
                        {row.qty} шт · {formatSum(row.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </section>

          {(o.catalog.withoutImages > 0 || o.catalog.withoutVariants > 0) && (
            <Card title="Здоровье витрины">
              <div className="space-y-1 text-sm">
                {o.catalog.withoutImages > 0 && (
                  <p>
                    <Badge tone="amber">{o.catalog.withoutImages}</Badge> активных товаров без
                    фотографий — в каталоге они выглядят заглушкой
                  </p>
                )}
                {o.catalog.withoutVariants > 0 && (
                  <p>
                    <Badge tone="red">{o.catalog.withoutVariants}</Badge> активных товаров без
                    вариаций — их нельзя добавить в корзину
                  </p>
                )}
              </div>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}

function Action({
  label,
  value,
  to,
  alarming,
}: {
  label: string;
  value: number;
  to: string;
  alarming?: boolean;
}) {
  const highlight = alarming && value > 0;

  return (
    <Link
      to={to}
      className={`rounded-xl border p-3 transition hover:shadow-sm ${
        highlight
          ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
          : 'border-neutral-200 dark:border-neutral-800'
      }`}
    >
      <p className={`text-2xl font-semibold ${highlight ? 'text-red-600 dark:text-red-400' : ''}`}>
        {value}
      </p>
      <p className="mt-0.5 text-xs text-neutral-500">{label}</p>
    </Link>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function Health({ data, loading }: { data?: ReadyResponse; loading: boolean }) {
  const dot = (state?: 'up' | 'down') =>
    loading || !state ? 'bg-neutral-400' : state === 'up' ? 'bg-emerald-500' : 'bg-red-500';

  return (
    <div className="flex items-center gap-3 text-xs text-neutral-500">
      <span className="flex items-center gap-1.5">
        <span className={`inline-block size-2.5 rounded-full ${dot(data?.services?.database)}`} />
        база
      </span>
      <span className="flex items-center gap-1.5">
        <span className={`inline-block size-2.5 rounded-full ${dot(data?.services?.redis)}`} />
        redis
      </span>
    </div>
  );
}
